import { useState } from 'react'
import { Badge, Button, Col, Container, Row } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import useMyProperties from '../../../Hooks/UseMyProperties/UseMyPropertiesHook'
import './user.scss'
import AccessLinkButton from '../../../Components/Buttons/AccessLinkButton'
import canAccess from '../../../services/accessService/accessService'
import QuickForm from '../../../Components/Forms/QuickForm/QuickForm'
import { userFieldRoles } from '../../../Components/Forms/EditUserForm/EditUserFormHelpers'
import { useAddResourceAccessMutation, useDeleteResourceAccessMutation, useEditResourceAccessMutation, useGetResourceAccessListQuery, useGetUserInfoQuery, useGetBuildingsQuery } from '../../../services/bClientApi'
import { FieldGeneratorFieldType } from '../../../Components/FormFields/FieldGenerator/FieldGenerator'
import { textBooleanHandler } from '../../../Helpers/ValueHandlers/boolHandler'
import { ResourceAccessType } from '../../../Types/resourceAccessType'
import ProfileInformation from '../../../Components/Users/ProfileInformation'
import { BuildingEntryType } from '../../../Types/buildingTypes'
import Collapsible from '../../../Components/Collapsible/Collapsible'

export default function UserPage() {

  const { userId = '' } = useParams()

  const {
    data,
    error,
    isLoading,
    isFetching,
  } = useGetUserInfoQuery(userId, { skip: !userId })
  const { _id, email, firstName, lastName, role, profilePic, phone, lastLogin } = data || {}

  const {
    data: userPropertyResourceAccess,
    error: userPropertyResourceAccessError,
    isLoading: userPropertyResourceAccessLoading,
    isFetching: userPropertyResourceAccessFetching,
  } = useGetResourceAccessListQuery({
    resourceAccessQuery: {
      resourceType: 'property',
      userId: _id,
    },
    pagination: {
      page: 1,
      limit: 150,
      all: true,
    }
  }, {
    skip: isLoading || isFetching || !_id,
  })

  const userPropertyResourceAccessList = userPropertyResourceAccess?.docs || []
  const userPropertyIdKeys: {
    [key: string]: any
  } = {}

  userPropertyResourceAccessList.forEach((resourceAccessInfo: any) => {
    userPropertyIdKeys[resourceAccessInfo.resourceId] = resourceAccessInfo
  })

  // Building resource access for this user
  const {
    data: userBuildingResourceAccess,
    error: userBuildingResourceAccessError,
    isLoading: userBuildingResourceAccessLoading,
    isFetching: userBuildingResourceAccessFetching,
  } = useGetResourceAccessListQuery({
    resourceAccessQuery: {
      resourceType: 'building',
      userId: _id,
    },
    pagination: {
      page: 1,
      limit: 150,
      all: true,
    }
  }, {
    skip: isLoading || isFetching || !_id,
  })

  const userBuildingResourceAccessList = userBuildingResourceAccess?.docs || []
  const userBuildingIdKeys: {
    [key: string]: any
  } = {}

  userBuildingResourceAccessList.forEach((resourceAccessInfo: any) => {
    userBuildingIdKeys[resourceAccessInfo.resourceId] = resourceAccessInfo
  })

  // Fetch all buildings
  const {
    data: buildingsData,
    isLoading: buildingsLoading,
    isFetching: buildingsFetching,
  } = useGetBuildingsQuery({
    buildingsQuery: {},
    pagination: {
      page: 1,
      limit: 100
    }
  },
    { skip: isLoading || isFetching }
  )

  const allBuildings = buildingsData?.docs || []

  const [
    addNewResourceAccess,
    {
      isLoading: isAdding,
      error: addError,
    }
  ] = useAddResourceAccessMutation()
  const [
    deleteResourceAccess,
    {
      isLoading: isDeleting,
      error: deleteError,
    }
  ] = useDeleteResourceAccessMutation()

  const [
    editResourceAccess,
    {
      isLoading: isEditing,
      error: editError
    }
  ] = useEditResourceAccessMutation()



  const {
    myProperties,
    isLoading: myPropertiesLoading,
    isFetching: myPropertiesFetching
  } = useMyProperties()

  const [formData, setFormData] = useState({
    roleOverride: null as string | null,
    quickFilter: '',
    authorizedOnly: true,
    publicOnly: true,
  })

  const [buildingFormData, setBuildingFormData] = useState({
    quickFilter: '',
    authorizedOnly: true,
    publicOnly: true,
  })

  const processedMyProperties = myProperties ? Object.keys(myProperties).map((pId: string) => {
    const p = myProperties[pId]
    const processedEntry = {
      propertyInfo: p,
      propertyShortName: p.title.split(' - ')[0],
      authorized: false,
      role: '',
      accessRules: {},
    }

    const resourceAccessInfo = userPropertyIdKeys[pId]
    if (resourceAccessInfo) {
      processedEntry.authorized = true
      processedEntry.role = resourceAccessInfo.role
      processedEntry.accessRules = resourceAccessInfo?.accessRules ? resourceAccessInfo.accessRules : {}
    }

    return processedEntry
  }) : []

  const filteredProperties = !processedMyProperties ? [] : processedMyProperties.filter((property) => {
    // Filter by public only
    if (formData.publicOnly && !property.propertyInfo.public) {
      return false
    }
    // Filter by authorization status
    if (formData.authorizedOnly && !property.authorized) {
      return false
    }
    // Filter by quick filter text
    if (formData.quickFilter && !property.propertyInfo.title.toLowerCase().includes(formData.quickFilter.toLowerCase())) {
      return false
    }
    return true
  })

  const toggleUserOnProperty = async (propertyId: string) => {
    const resourceAccessInfo = userPropertyIdKeys[propertyId]
    const isAuthorized = resourceAccessInfo ? true : false
    const newResourceAccessInfo = {
      resourceType: 'property',
      resourceId: propertyId,
      userId: _id,
      role: formData.roleOverride || role,
    }
    if (isAuthorized) {
      await deleteResourceAccess(resourceAccessInfo._id).unwrap()
    } else {
      await addNewResourceAccess(newResourceAccessInfo).unwrap()
    }
  }

  const toggleAccessRulesOnProperty = async (propertyId: string, accessRule: string) => {
    const resourceAccess = userPropertyIdKeys[propertyId] as ResourceAccessType | undefined
    if (!resourceAccess) return
    const {
      _id = '',
      accessRules = {}
    } = resourceAccess || {}
    if (!_id) return
    const newAccessRules: any = { ...accessRules }
    if (newAccessRules[accessRule]) {
      delete newAccessRules[accessRule]
    } else {
      newAccessRules[accessRule] = true
    }
    await editResourceAccess({
      resourceAccessId: _id,
      resourceAccessInfo: {
        accessRules: newAccessRules
      }
    }).unwrap()
  }

  // Process buildings for display
  interface ProcessedBuilding {
    buildingInfo: BuildingEntryType,
    authorized: boolean,
    role: string,
  }

  const processedBuildings: ProcessedBuilding[] = allBuildings.map((building: BuildingEntryType) => {
    const buildingId = building._id || ''
    const processedEntry: ProcessedBuilding = {
      buildingInfo: building,
      authorized: false,
      role: '',
    }

    const resourceAccessInfo = userBuildingIdKeys[buildingId]
    if (resourceAccessInfo) {
      processedEntry.authorized = true
      processedEntry.role = resourceAccessInfo.role
    }

    return processedEntry
  })

  const filteredBuildings = processedBuildings.filter((building: ProcessedBuilding) => {
    // Filter by public only
    if (buildingFormData.publicOnly && !building.buildingInfo.public) {
      return false
    }
    // Filter by authorization status
    if (buildingFormData.authorizedOnly && !building.authorized) {
      return false
    }
    // Filter by quick filter text
    if (buildingFormData.quickFilter && !building.buildingInfo.title.toLowerCase().includes(buildingFormData.quickFilter.toLowerCase())) {
      return false
    }
    return true
  })

  const toggleUserOnBuilding = async (buildingId: string) => {
    const resourceAccessInfo = userBuildingIdKeys[buildingId]
    const isAuthorized = resourceAccessInfo ? true : false
    const newResourceAccessInfo = {
      resourceType: 'building',
      resourceId: buildingId,
      userId: _id,
      role: formData.roleOverride || role,
    }
    if (isAuthorized) {
      await deleteResourceAccess(resourceAccessInfo._id).unwrap()
    } else {
      await addNewResourceAccess(newResourceAccessInfo).unwrap()
    }
  }

  const disableButtons = myPropertiesLoading === true || isLoading || myPropertiesFetching === true || isFetching === true || isAdding === true || isDeleting === true || isEditing === true || userPropertyResourceAccessLoading === true || userPropertyResourceAccessFetching === true || userBuildingResourceAccessLoading === true || userBuildingResourceAccessFetching === true || buildingsLoading === true || buildingsFetching === true

  return (
    <Container>
      <Row>
        <Col>
          <h1>User Page</h1>
          <hr />
          {error && (<>{JSON.stringify(error)}<br /></>)}
          {userPropertyResourceAccessError && (<>{JSON.stringify(userPropertyResourceAccessError)}<br /></>)}
          {userBuildingResourceAccessError && (<>{JSON.stringify(userBuildingResourceAccessError)}<br /></>)}
          {addError && (<>{JSON.stringify(addError)}<br /></>)}
          {editError && (<>{JSON.stringify(editError)}<br /></>)}
          {deleteError && (<>{JSON.stringify(deleteError)}<br /></>)}

          {/* User Profile Information */}
          {data && (
            <ProfileInformation
              username={email}
              firstName={firstName}
              lastName={lastName}
              role={role}
              phone={phone}
              lastLogin={lastLogin}
              profilePic={profilePic}
            />
          )}

          <div>
            <AccessLinkButton
              color={'secondary'}
              action={'edit-user'}
              size={'sm'}
              to={`/edit-user/${_id}`}
              text={'Edit User'}
            />
            <AccessLinkButton
              color={'danger'}
              action={'delete-user'}
              size={'sm'}
              to={`/delete-user/${_id}`}
              text={'Delete User'}
            />
          </div>

          <hr />
          <h4 className="mb-3">Role Override</h4>
          <QuickForm
            initialData={{
              roleOverride: formData.roleOverride,
            }}
            fields={[formFields[0]]}
            onChange={(data: any) => {
              const fieldName = data.target.name
              const fieldValue = data.target.value
              if (filterFields[fieldName]) {
                const newFormData = {
                  ...formData,
                  [fieldName]: filterFields[fieldName].handler ? filterFields[fieldName].handler(fieldValue) : fieldValue,
                }
                setFormData(newFormData)
              }
            }} />

          <hr />
          <Collapsible
            title={`Property Access (${filteredProperties.length})`}
            initialValue={false}
            openText="Show"
            closeText="Hide"
          >
            <div className="property-access-section">
              <QuickForm
                initialData={{
                  quickFilter: formData.quickFilter,
                  authorizedOnly: formData.authorizedOnly,
                  publicOnly: formData.publicOnly,
                }}
                fields={propertyFilterFormFields}
                onChange={(data: any) => {
                  const fieldName = data.target.name
                  const fieldValue = data.target.value
                  if (filterFields[fieldName]) {
                    const newFormData = {
                      ...formData,
                      [fieldName]: filterFields[fieldName].handler ? filterFields[fieldName].handler(fieldValue) : fieldValue,
                    }
                    setFormData(newFormData)
                  }
                }} />

              <hr />
              {filteredProperties.map((p: any) => {

                const {
                  authorized,
                  role,
                  propertyInfo,
                  propertyShortName,
                  accessRules = {},
                } = p

                return (
                  <div key={propertyInfo._id} className="user-property-add-remove-entry">
                    <div className="property-title d-flex flex-wrap align-items-center gap-2">
                      {/* {p.propertyInfo.title}  */}
                      <div>
                        {propertyShortName}
                      </div>
                      <div>
                        <Badge className="text-uppercase">
                          {role ? `${role}` : 'No Role'}
                        </Badge>
                      </div>
                    </div>
                    {canAccess('user', 'authorizeProperty') &&
                      <div className="add-remove-button d-flex flex-wrap gap-2">
                        <Button
                          className="me-4"
                          size="sm"
                          variant={authorized ? 'success' : 'danger'}
                          disabled={disableButtons}
                          onClick={() => {
                            toggleUserOnProperty(propertyInfo._id)
                          }}>
                          {authorized ? '✅ Access' : '🚫 Access'}
                        </Button>
                        <div className="d-flex flex-wrap gap-2">
                          {/* toggle hideCleaning */}

                          {authorized &&
                            <div className="d-flex align-items-center">
                              <Button
                                size="sm"
                                role={'button'}
                                variant={accessRules.hideCleaning ? 'outline-secondary' : 'outline-success'}
                                onClick={() => {
                                  toggleAccessRulesOnProperty(propertyInfo._id, 'hideCleaning')
                                }}>
                                {accessRules.hideCleaning ? '🚫 Cleaning🧹' : '✅ Cleaning🧹'}
                              </Button>
                            </div>
                          }

                          {/* toggle hideBookingGuest */}
                          {authorized &&
                            <div className="d-flex align-items-center">
                              <Button
                                size="sm"
                                role={'button'}
                                variant={accessRules.hideBookingGuest ? 'outline-secondary' : 'outline-success'}
                                onClick={() => {
                                  toggleAccessRulesOnProperty(propertyInfo._id, 'hideBookingGuest')
                                }}>
                                {accessRules.hideBookingGuest ? ' 🚫 Booking Guest💁' : '✅ Booking Guest💁'}
                              </Button>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                )
              })}
            </div>
          </Collapsible>

          <Collapsible
            title={`Building Access (${filteredBuildings.length})`}
            initialValue={false}
            openText="Show"
            closeText="Hide"
          >
            <div className="building-access-section">
              <QuickForm
                initialData={{
                  quickFilter: buildingFormData.quickFilter,
                  authorizedOnly: buildingFormData.authorizedOnly,
                  publicOnly: buildingFormData.publicOnly,
                }}
                fields={buildingFormFields}
                onChange={(data: any) => {
                  const fieldName = data.target.name
                  const fieldValue = data.target.value
                  if (buildingFilterFields[fieldName]) {
                    const newFormData = {
                      ...buildingFormData,
                      [fieldName]: buildingFilterFields[fieldName].handler ? buildingFilterFields[fieldName].handler(fieldValue) : fieldValue,
                    }
                    setBuildingFormData(newFormData)
                  }
                }} />

              <hr />
              {filteredBuildings.map((b: any) => {
                const {
                  authorized,
                  role,
                  buildingInfo,
                } = b

                const buildingId = buildingInfo._id || ''

                return (
                  <div key={buildingId} className="user-building-add-remove-entry">
                    <div className="building-title d-flex flex-wrap align-items-center gap-2">
                      <div>
                        {buildingInfo.title}
                      </div>
                      <div>
                        <Badge className="text-uppercase">
                          {role ? `${role}` : 'No Role'}
                        </Badge>
                      </div>
                    </div>
                    {canAccess('user', 'authorizeProperty') &&
                      <div className="add-remove-button d-flex flex-wrap gap-2">
                        <Button
                          className="me-4"
                          size="sm"
                          variant={authorized ? 'success' : 'danger'}
                          disabled={disableButtons}
                          onClick={() => {
                            toggleUserOnBuilding(buildingId)
                          }}>
                          {authorized ? '✅ Access' : '🚫 Access'}
                        </Button>
                      </div>
                    }
                  </div>
                )
              })}
            </div>
          </Collapsible>
        </Col>
      </Row>
    </Container>
  )
}

const filterFields: any = {
  roleOverride: 1,
  quickFilter: 1,
  authorizedOnly: {
    handler: textBooleanHandler
  },
  publicOnly: {
    handler: textBooleanHandler
  },
}

const formFields: FieldGeneratorFieldType[] = [
  {
    fieldType: 'select',
    fieldLabel: 'Role Override',
    fieldName: 'roleOverride',
    options: userFieldRoles()
  },
]

const propertyFilterFormFields: FieldGeneratorFieldType[] = [
  {
    fieldName: 'quickFilter',
    fieldLabel: 'Property Filter',
    fieldType: 'text',
  },
  {
    fieldName: 'authorizedOnly',
    fieldLabel: 'Authorized Only',
    fieldType: 'checkbox',
    required: false
  },
  {
    fieldName: 'publicOnly',
    fieldLabel: 'Public Only',
    fieldType: 'checkbox',
    required: false
  },
]

const buildingFilterFields: any = {
  quickFilter: 1,
  authorizedOnly: {
    handler: textBooleanHandler
  },
  publicOnly: {
    handler: textBooleanHandler
  },
}

const buildingFormFields: FieldGeneratorFieldType[] = [
  {
    fieldName: 'quickFilter',
    fieldLabel: 'Building Filter',
    fieldType: 'text',
  },
  {
    fieldName: 'authorizedOnly',
    fieldLabel: 'Authorized Only',
    fieldType: 'checkbox',
    required: false
  },
  {
    fieldName: 'publicOnly',
    fieldLabel: 'Public Only',
    fieldType: 'checkbox',
    required: false
  },
]
